import { useCallback, useState } from "react";
import type { FileRejection } from "react-dropzone";
import { useDropzone } from "react-dropzone";
import { t } from "@/i18n-js/instance";
import { useUppyUpload } from "@/react/components/Spaces/CourseSpace/Lessons/Edit/Content/LessonContent/useUppyUpload";
import { useToast } from "@circle-react-shared/uikit/ToastV2";

export const MAX_FILE_SIZE = 50 * 1024 * 1024;

export interface InlineAttachment {
  byte_size?: number;
  filename?: string;
  signed_id?: string;
  url?: string;
}

export interface MediaPickerProps {
  onUploadComplete: (file: InlineAttachment) => void;
  onUploadStarted: () => void;
}

export const useMediaPicker = ({
  onUploadStarted,
  onUploadComplete,
}: MediaPickerProps) => {
  const toast = useToast();

  const { uppy, isUploading, progress } = useUppyUpload({
    onUploadComplete,
    onUploadStarted,
  });
  const [fileSize, setFileSize] = useState<number>(0);

  const handleUpload = (files: File[]) => {
    uppy.getFiles().forEach(file => uppy.removeFile(file.id));
    files.forEach(file => {
      uppy.addFile({
        name: file.name,
        type: file.type,
        data: file,
      });
      setFileSize(file.size);
    });
  };

  const handleFileRejection = useCallback(
    (errors: FileRejection[]) => {
      if (!errors.length) {
        return;
      }

      if (errors[0].errors[0].code === "file-too-large") {
        toast.error(
          t("builder_ui.prop_type.attachment_upload.errors.file_too_large"),
        );
        return;
      }

      toast.error(
        t("builder_ui.prop_type.attachment_upload.errors.invalid_file"),
      );
    },
    [toast],
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    onDropAccepted: handleUpload,
    onDropRejected: handleFileRejection,
    noClick: true,
    disabled: isUploading,
    maxFiles: 1,
    multiple: false,
    maxSize: MAX_FILE_SIZE,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".gif"],
      "video/*": [".mp4", ".mov", ".avi"],
      "audio/*": [".mp3", ".wav", ".m4a"],
      "application/*": [
        ".pdf",
        ".doc",
        ".docx",
        ".xls",
        ".xlsx",
        ".ppt",
        ".pptx",
        ".csv",
      ],
    },
  });

  const onSelectFile = () => {
    open();
  };

  return {
    getRootProps,
    getInputProps,
    progress,
    isUploading,
    onSelectFile,
    fileSize,
  };
};
